import React, { useLayoutEffect, useRef } from 'react';
import {
  Flex,
  Text,
  CalendarIcon,
  PaperclipIcon,
  ProviderConsumer as FluentUIThemeConsumer
} from '@fluentui/react-northstar';
import TicketSVG from '../../../../../svg/ticket.svg';
import CommentSVG from '../../../../../svg/comments.svg';
import CollaboratorIcon from '../../../../../svg/collaboratorIcon.svg';
import AffectedUserIcon from '../../../../../svg/affected-user.svg';
import { DateTime } from 'luxon';
import { threedigitized } from '../../../ticketHelper';
import { TicketStatus } from '../../../../shared/components/TicketCardTemplate';
import { UsersTooltip } from '../../../../shared/components/TicketDetailInfo/UsersTooltip';
import { CheckFeature } from '../../../../shared/components/CheckFeature';
import { LICENSE_FEATURE } from '../../../../shared/utils/constants';
import { DisplayLifecycleWithPhases } from '../../../../shared/components/TicketDetailInfo/displayLifecyclePhases/DisplayLifecycleWithPhases';
import { ConvertDateToLocale, DateTimeSeparator } from '../../../../shared/common/ConvertDate';
import { dropToFit } from '../../../../shared/components/TicketDetailInfo/dropToFit';
import { useTranslation } from 'react-i18next';
import { TicketLifecycleDisplay } from '../../TicketRequestCard/TicketRequestCard';

interface Props {
  ticketData: Ticket;
  attachmentsCount: number;
  commentsCount: number;
}

export const RequestDetailCard = ({ ticketData, commentsCount, attachmentsCount }: Props) => {
  const { t } = useTranslation();
  const rowRef = useRef<HTMLDivElement>(null);
  const dateRef = useRef<HTMLSpanElement>(null);
  const timeRef = useRef<HTMLSpanElement>(null);

  // The modified time - with its separator - shows only while the line has room for it; otherwise
  // the date stands alone rather than wrapping the time onto a second line.
  useLayoutEffect(() => {
    const row = rowRef.current;
    const dateEl = dateRef.current;
    const timeEl = timeRef.current;
    if (!row || !dateEl || !timeEl) return undefined;

    const fit = () => {
      dropToFit(
        [timeEl],
        () => 1,
        el => { el.style.display = ''; },
        el => { el.style.display = 'none'; },
        () => dateEl.scrollWidth > dateEl.clientWidth
      );
    };

    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(row);
    observer.observe(dateEl);
    return () => observer.disconnect();
  }, [ticketData?.ModifiedDate]);

  const counters = (
    <Flex vAlign="center" gap="gap.smaller">
      <CheckFeature featureName={LICENSE_FEATURE.Collaborators}>
        <Flex vAlign="center">
          <CollaboratorIcon width={12} height={12} style={{ margin: '0 auto', color: '#8f90a6' }} />
          <UsersTooltip ticketCollaborator={ticketData?.TicketCollaborators.map(y => y.PlatformUser)} title={t('ticket.ticket-list.collaborators')} />
        </Flex>
      </CheckFeature>

      <CheckFeature featureName={LICENSE_FEATURE.AffectedUsers}>
        <Flex vAlign="center">
          <AffectedUserIcon width={12} height={12} style={{ margin: '0 auto', color: '#8f90a6' }}/>
          <UsersTooltip ticketCollaborator={ticketData?.AffectedUsers.map(y => y.PlatformUser)} title={t('ticket.ticket-list.affected-users')} />
        </Flex>
      </CheckFeature>
      <Flex vAlign="center">
        <PaperclipIcon size="smaller" style={{ color: '#8f90a6' }} />
        <Text size="small" style={{ color: '#8f90a6', marginLeft: '4px' }}>
          {attachmentsCount ?? 0}
        </Text>
      </Flex>

      <Flex vAlign="center">
        <CommentSVG
          width={12}
          height={12}
          style={{ margin: '0 auto', color: '#8f90a6' }}
        />
        <Text size="small" style={{ color: '#8f90a6', marginLeft: '4px' }}>
          {commentsCount ?? 0}
        </Text>
      </Flex>
    </Flex>
  );

  return (
    <FluentUIThemeConsumer
      render={globalTheme => (
        <Flex
          column
          gap="gap.small"
          style={{
            backgroundColor: globalTheme.siteVariables.colorScheme.default.background,
            padding: '1.56rem',
            marginBottom: '1rem',
            boxShadow: '0px 0.3px 0.9px rgba(0, 0, 0, 0.07), 0px 1.6px 3.6px rgba(0, 0, 0, 0.11)',
            borderRadius: '4px'
          }}
          className='print-hidden'
        >
          <Flex space="between" vAlign="center">
            <Flex vAlign="center">
              <TicketSVG width={24} className={`mr-3`} />#
              {threedigitized(ticketData['Id'])}
            </Flex>
            <TicketStatus status={ticketData.Status} />
          </Flex>

          <Text weight="semibold" className="break-all">{ticketData.Title}</Text>

          {ticketData?.TicketLifecycle?.Id && (
            <>
              <div className='hidden md:block'>
                <DisplayLifecycleWithPhases ticketData={ticketData} isAnalyst={false} />
              </div>
              <div className='block md:hidden'>
                <TicketLifecycleDisplay ticket={ticketData} />
              </div>
            </>
          )}
          {/* When the ticket last changed. Plain divs rather than Flex:
              Northstar's Flex sets display itself, so a Tailwind display class on it is ignored.
              On a phone the counters go on the line below, so the date has the width to itself. */}
          <div
            ref={rowRef}
            className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between"
            style={{ paddingTop: '16px', borderTop: '1px solid lightgrey' }}
          >
            <div className="flex items-center min-w-0">
              <span className="hidden md:inline-flex mr-3">
                <CalendarIcon className={`text-gray-500`} />
              </span>
              {/* 1px left: the M's own side bearing starts its ink 1.12px in (Segoe UI 12px,
                  measured), which left the label short of the counter icon below it on a phone. */}
              <Text size="small" className="text-gray-500 flex-none mr-2" style={{ marginLeft: '-1px' }}>
                {t('requests.modified')}:
              </Text>
              <span ref={dateRef} className='text-xs truncate' style={{ minWidth: 0 }}>
                {ConvertDateToLocale(ticketData.ModifiedDate, DateTime.DATE_MED_WITH_WEEKDAY)}
                {ticketData.ModifiedDate && (
                  <span ref={timeRef}>{DateTimeSeparator()}{ConvertDateToLocale(ticketData.ModifiedDate, DateTime.TIME_SIMPLE)}</span>
                )}
              </span>
            </div>
            {counters}
          </div>
        </Flex>
      )}
    />
  );
};
